import type { AppVueRouter } from '@modules/core/services/router/types';
import type { TranslationKeysMapper } from '@modules/i18n/types';
import type { AppMenu } from '@modules/sidebar/utils';
import { AppModule } from '@modules/core/plugins/AppModule';
import { useI18n } from '@modules/i18n/composables';
import { ADMIN_FINANCE_MENU_ITEM, TRANSLATION_GROUP_SIDEBAR } from '@modules/sidebar/constants';

import {
  ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS_LIST_ROUTE_NAME,
  ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS_LIST_VIEW_ROUTE_NAME,
  ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS_ROUTE_NAME,
  ADMIN_PAYMENT_SYSTEM_DETAILS_REQUESTS_HISTORY_LIST_ROUTE_NAME,
  ADMIN_PAYMENT_SYSTEM_DETAILS_REQUESTS_HISTORY_LIST_VIEW_ROUTE_NAME,
  ADMIN_PAYMENT_SYSTEM_DETAILS_REQUESTS_HISTORY_ROUTE_NAME,
  PAYMENT_SYSTEM_DETAILS_PERMISSIONS,
  TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS,
  TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_REQUESTS_HISTORY,
  TRANSLATION_GROUP_AFFILIATE_PAYMENT_SYSTEM_PROFILE,
} from './constants';
import { getAffiliatePaymentSystemsTranslationKeys, getTranslationKeysPendingRequests, getTranslationKeysRequestsHistory } from './translations';

export class PaymentSystemDetailsModule extends AppModule {
  addRoutes(router: AppVueRouter): void {
    router.addAdminRoute({
      name: ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS_ROUTE_NAME,
      path: 'payment-details-pending-requests',
      permission: PAYMENT_SYSTEM_DETAILS_PERMISSIONS.PENDING_LIST_SHOW,
      component: () => import('@modules/core/components/SimpleChildRoutePage/SimpleChildRoutePage.vue'),
      keepAliveInclude: 'PaymentSystemDetailsPendingListPage',
    });

    router.addChildRoute(ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS_ROUTE_NAME, {
      name: ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS_LIST_ROUTE_NAME,
      path: '',
      permission: PAYMENT_SYSTEM_DETAILS_PERMISSIONS.PENDING_LIST_SHOW,
      component: () => import('./pages/PaymentSystemDetailsPendingListPage/PaymentSystemDetailsPendingListPage.vue'),
    });

    router.addChildRoute(ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS_LIST_ROUTE_NAME, {
      name: ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS_LIST_VIEW_ROUTE_NAME,
      path: 'review/:id',
      permission: PAYMENT_SYSTEM_DETAILS_PERMISSIONS.SHOW,
      components: {
        PaymentSystemDetailsView: () => import('./pages/PaymentSystemDetailsViewPage/PaymentSystemDetailsViewPage.vue'),
      },
    });

    router.addAdminRoute({
      name: ADMIN_PAYMENT_SYSTEM_DETAILS_REQUESTS_HISTORY_ROUTE_NAME,
      path: 'payment-details-requests-history',
      permission: PAYMENT_SYSTEM_DETAILS_PERMISSIONS.HISTORY_LIST_SHOW,
      component: () => import('@modules/core/components/SimpleChildRoutePage/SimpleChildRoutePage.vue'),
      keepAliveInclude: 'PaymentSystemDetailsHistoryListPage',
    });

    router.addChildRoute(ADMIN_PAYMENT_SYSTEM_DETAILS_REQUESTS_HISTORY_ROUTE_NAME, {
      name: ADMIN_PAYMENT_SYSTEM_DETAILS_REQUESTS_HISTORY_LIST_ROUTE_NAME,
      path: '',
      permission: PAYMENT_SYSTEM_DETAILS_PERMISSIONS.HISTORY_LIST_SHOW,
      component: () => import('./pages/PaymentSystemDetailsHistoryListPage/PaymentSystemDetailsHistoryListPage.vue'),
    });

    router.addChildRoute(ADMIN_PAYMENT_SYSTEM_DETAILS_REQUESTS_HISTORY_LIST_ROUTE_NAME, {
      name: ADMIN_PAYMENT_SYSTEM_DETAILS_REQUESTS_HISTORY_LIST_VIEW_ROUTE_NAME,
      path: 'view/:id',
      permission: PAYMENT_SYSTEM_DETAILS_PERMISSIONS.SHOW,
      components: {
        PaymentSystemDetailsView: () => import('./pages/PaymentSystemDetailsViewPage/PaymentSystemDetailsViewPage.vue'),
      },
    });
  }

  addMenu(menu: AppMenu): void {
    const { getTPath } = useI18n();

    menu.addChildAdminMenuItem(ADMIN_FINANCE_MENU_ITEM, {
      caption: getTPath(TRANSLATION_GROUP_SIDEBAR, 'FINANCE_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS'),
      to: {
        name: ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS_LIST_ROUTE_NAME,
      },
      permission: PAYMENT_SYSTEM_DETAILS_PERMISSIONS.PENDING_LIST_SHOW,
      id: ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS_LIST_ROUTE_NAME,
      order: 10,
    });

    menu.addChildAdminMenuItem(ADMIN_FINANCE_MENU_ITEM, {
      caption: getTPath(TRANSLATION_GROUP_SIDEBAR, 'FINANCE_PAYMENT_SYSTEM_DETAILS_REQUESTS_HISTORY'),
      to: {
        name: ADMIN_PAYMENT_SYSTEM_DETAILS_REQUESTS_HISTORY_LIST_ROUTE_NAME,
      },
      permission: PAYMENT_SYSTEM_DETAILS_PERMISSIONS.HISTORY_LIST_SHOW,
      id: ADMIN_PAYMENT_SYSTEM_DETAILS_REQUESTS_HISTORY_LIST_ROUTE_NAME,
      order: 20,
    });
  }

  addTranslationKeys(mapper: TranslationKeysMapper): void {
    mapper.addTranslationsMap(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, getTranslationKeysPendingRequests);
    mapper.addTranslationsMap(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_REQUESTS_HISTORY, getTranslationKeysRequestsHistory);
    mapper.addTranslationsMap(TRANSLATION_GROUP_AFFILIATE_PAYMENT_SYSTEM_PROFILE, getAffiliatePaymentSystemsTranslationKeys);
  }
}
